import React, { memo, useCallback, useMemo } from 'react';

import { FORM_FIELD_TYPE } from 'app/pages/system/aiProcesses/AiProcessPage/tabs/formSchema/formFieldTypes';
import {
  AutocompleteCDS,
  DateFieldCDS,
  RadioGroupCDS,
  TextFieldCDS,
} from 'app/shared/componentsRestyled/coreDesignSystem/form';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { COMMON_DATE_FORMAT } from 'constants/date';

import { FORM_REVIEW_LABELS, FormReviewTestIds } from '../../FormReviewCard.constants';

const BOOLEAN_OPTIONS = [
  { value: 'true', label: FORM_REVIEW_LABELS.BOOLEAN_TRUE },
  { value: 'false', label: FORM_REVIEW_LABELS.BOOLEAN_FALSE },
];

const toEnumOptions = allowedValues =>
  (allowedValues ?? []).map(allowed => ({ value: allowed, name: allowed }));

const FormFieldOverrideInput = ({ schemaField, value, disabled, onChange }) => {
  const type = schemaField?.type ?? FORM_FIELD_TYPE.TEXT;

  const enumOptions = useMemo(
    () => toEnumOptions(schemaField?.allowedValues),
    [schemaField?.allowedValues],
  );

  const selectedEnumOption = useMemo(
    () => enumOptions.find(option => option.value === value) ?? null,
    [enumOptions, value],
  );

  const onTextChange = useCallback(event => onChange(event.target.value), [onChange]);

  const onRadioChange = useCallback(event => onChange(event.target.value), [onChange]);

  const onEnumChange = useCallback((_event, option) => onChange(option?.value ?? ''), [onChange]);

  const onDateChange = useCallback(
    nextDate => onChange(nextDate ? dayjs(nextDate).format(COMMON_DATE_FORMAT) : ''),
    [onChange],
  );

  if (type === FORM_FIELD_TYPE.BOOLEAN) {
    return (
      <RadioGroupCDS
        row
        dataTestId={FormReviewTestIds.OVERRIDE_BOOLEAN}
        disabled={disabled}
        name="form-field-override-boolean"
        options={BOOLEAN_OPTIONS}
        value={value}
        onChange={onRadioChange}
      />
    );
  }

  if (type === FORM_FIELD_TYPE.ENUM) {
    return (
      <AutocompleteCDS
        dataTestId={FormReviewTestIds.OVERRIDE_ENUM}
        disabled={disabled}
        label={FORM_REVIEW_LABELS.OVERRIDE_VALUE_LABEL}
        name="form-field-override-enum"
        options={enumOptions}
        value={selectedEnumOption}
        onChange={onEnumChange}
      />
    );
  }

  if (type === FORM_FIELD_TYPE.DATE) {
    return (
      <DateFieldCDS
        dataTestId={FormReviewTestIds.OVERRIDE_DATE}
        disabled={disabled}
        name="form-field-override-date"
        value={value || null}
        onChange={onDateChange}
      />
    );
  }

  const isNumeric = type === FORM_FIELD_TYPE.NUMBER || type === FORM_FIELD_TYPE.CURRENCY;

  return (
    <TextFieldCDS
      fullWidth
      data-testid={isNumeric ? FormReviewTestIds.OVERRIDE_NUMBER : FormReviewTestIds.OVERRIDE_INPUT}
      disabled={disabled}
      label={FORM_REVIEW_LABELS.OVERRIDE_VALUE_LABEL}
      name="form-field-override-text"
      placeholder={FORM_REVIEW_LABELS.OVERRIDE_PLACEHOLDER}
      size="small"
      type={isNumeric ? 'number' : 'text'}
      value={value}
      onChange={onTextChange}
    />
  );
};

FormFieldOverrideInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  schemaField: PropTypes.shape({
    type: PropTypes.string,
    allowedValues: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default memo(FormFieldOverrideInput);
